import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRight, Brain, MessageSquareText, Share2, ArrowRightLeft, Users, BadgeCheck } from 'lucide-react';
import LanguageSwitcher from '../common/LanguageSwitcher';

interface MobileNavProps {
  isOpen: boolean;
  onNavigate: () => void;
}

const featured = [
  { key: 'zimbraSovereignAI', to: '/products/zimbra-sovereign-ai', icon: Brain },
  { key: 'nextSolution', to: '/products/next', icon: MessageSquareText },
  { key: 'zimbraShareGateway', to: '/products/zimbra-share-gateway', icon: Share2 },
  { key: 'fcshare', to: '/products/fcshare', icon: BadgeCheck },
  { key: 'exchangeToZimbra', to: '/products/exchange-to-zimbra', icon: ArrowRightLeft },
  { key: 'ldapProvisioning', to: '/products/ldap-zimbra-provisioning', icon: Users },
];

const MobileNav: React.FC<MobileNavProps> = ({ isOpen, onNavigate }) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();

  const links = [
    { label: t('aiMenu.products'), to: '/products' },
    { label: t('aiMenu.services'), to: '/services' },
    { label: t('aiMenu.training'), to: '/training' },
    { label: t('aiMenu.scalenixUniversity.label', { defaultValue: 'ScaleNix University' }), to: '/university' },
    { label: t('nav.contact'), to: '/contact' },
  ];

  const go = (to: string) => {
    navigate(to);
    onNavigate();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.25 }}
          className="md:hidden overflow-hidden border-t border-white/10 bg-slate-950/95 backdrop-blur-xl"
        >
          <div className="max-h-[calc(100vh-80px)] overflow-y-auto px-4 py-4 space-y-6">
            <div className="grid grid-cols-2 gap-2">
              {links.map((link) => (
                <button
                  key={link.to}
                  onClick={() => go(link.to)}
                  className="flex items-center justify-between px-4 py-3 rounded-xl border border-white/10 bg-white/[0.03] text-sm font-semibold text-white hover:border-cyan-400/40 transition-colors"
                >
                  {link.label}
                  <ArrowRight className="h-4 w-4 text-cyan-300" />
                </button>
              ))}
            </div>

            <div>
              <p className="px-1 mb-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-cyan-300/80">
                {t('aiMenu.featuredProduct.eyebrow', { defaultValue: 'Highlights' })}
              </p>
              <div className="space-y-1">
                {featured.map(({ key, to, icon: Icon }) => (
                  <button
                    key={key}
                    onClick={() => go(to)}
                    className="flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-left hover:bg-white/5 transition-colors"
                  >
                    <span className="flex-shrink-0 w-9 h-9 rounded-lg bg-gradient-to-br from-blue-500/25 to-cyan-400/10 border border-white/10 flex items-center justify-center">
                      <Icon className="h-4 w-4 text-cyan-300" />
                    </span>
                    <span className="text-sm font-medium text-slate-200">{t(`aiMenu.${key}.label`)}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="pt-4 border-t border-white/10">
              <LanguageSwitcher />
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default MobileNav;
